/**
 * Browser softphone (SIP.js SimpleUser) for the UC platform.
 *
 * UX model:
 *   - Settings holds the ONE-TIME connect (login → device creds → register). On
 *     success the session (JWT + creds + URLs) is persisted to localStorage.
 *   - On every app open, if a saved session exists we auto-connect + register
 *     silently — the Dialer is the home screen, always ready.
 *   - The registration is kept alive: transport drops trigger an auto-reconnect
 *     loop (backoff), and we re-register on network-online / tab-visible so the
 *     phone is reachable for inbound calls whenever the app is open.
 *
 * Bottom-nav switches Dialer ⇄ Settings; an overlay handles ringing / active call.
 */
import { SimpleUser, type SimpleUserOptions } from 'sip.js/lib/platform/web';
import { SIPExtension } from 'sip.js/lib/api';
import { registerServiceWorker, subscribeToPush, onCallAction, startKeepAlive } from './pwa.js';
import {
  isNative, nativeRegister, nativeStop, nativeCall, nativeAnswer, nativeDecline,
  nativeHangup, nativeMute, nativeDtmf, onNativeEvent,
} from './native.js';

const $ = (id: string) => document.getElementById(id) as HTMLInputElement;
const out = (id: string) => document.getElementById(id) as HTMLElement;

const SESSION_KEY = 'uc_softphone_session_v1';

interface Credentials {
  extension: string;
  sipUsername: string;
  sipPassword: string;
  sipRealm: string;
  aor: string;
}
interface Session {
  apiUrl: string;
  wsUrl: string;
  token: string;
  deviceId: string;
  creds: Credentials;
}

let simpleUser: SimpleUser | null = null;
let session: Session | null = null;
let wantRegistered = false;         // desired state — drives the reconnect loop
let reconnecting = false;
let reconnectDelay = 2000;          // backoff, capped
let dialValue = '';
let pendingAnswer: 'answer' | 'decline' | null = null;  // intent from notification cold-start
let pendingDial: string | null = null;  // number to auto-dial once registered (?dial= from contacts/click-to-dial)
// Optional per-call outbound caller-ID (an E.164 DID the tenant owns). When set, we send it as
// the X-Preferred-Caller-ID header on the INVITE; sip-core validates ownership and stamps it as
// the outbound From. '' = use the tenant's default caller-ID (server-side).
let preferredCallerId = '';
let registered = false;                  // true between onRegistered and onUnregistered/disconnect
let inCall = false;                       // true between onCallAnswered and onCallHangup (a LIVE call,
                                          // independent of whether the dialer panel is open/closed)
let localAreaCode: string | null = null; // user's local area code (for 7-digit local dialing)

// ─── ringtone (Web Audio ring cadence + vibration) ───────────────────────────
let ringOsc: { stop: () => void } | null = null;
let ringVibrate: number | null = null;
function startRinging(): void {
  stopRinging();
  try {
    audioCtx ??= new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const ctx = audioCtx;
    // North-American ring: 440+480 Hz, 2s on / 4s off. Loop with scheduled bursts.
    let stopped = false;
    const scheduleBurst = (): void => {
      if (stopped) return;
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.05);
      gain.gain.setValueAtTime(0.2, now + 1.9);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
      gain.connect(ctx.destination);
      for (const f of [440, 480]) {
        const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = f;
        o.connect(gain); o.start(now); o.stop(now + 2.0);
      }
    };
    scheduleBurst();
    const iv = setInterval(scheduleBurst, 6000); // 2s ring + 4s silence
    ringOsc = { stop: () => { stopped = true; clearInterval(iv); } };
  } catch { /* audio unavailable */ }
  // Sustained vibration cadence (Android): buzz-pause repeating.
  if ('vibrate' in navigator) {
    const buzz = (): void => { navigator.vibrate?.([600, 400, 600, 400, 600]); };
    buzz();
    ringVibrate = window.setInterval(buzz, 3000);
  }
}
function stopRinging(): void {
  if (ringOsc) { ringOsc.stop(); ringOsc = null; }
  if (ringVibrate !== null) { clearInterval(ringVibrate); ringVibrate = null; }
  if ('vibrate' in navigator) navigator.vibrate?.(0);
}

// ─── DTMF key tones (local audible feedback, Web Audio dual-tone) ────────────
// The classic DTMF matrix: each key = one low + one high frequency.
const DTMF_FREQS: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};
let audioCtx: AudioContext | null = null;
function playKeyTone(key: string): void {
  const freqs = DTMF_FREQS[key];
  if (!freqs) return;
  try {
    // Lazily create on first user gesture (autoplay policy).
    audioCtx ??= new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const now = audioCtx.currentTime;
    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16); // short beep
    gain.connect(audioCtx.destination);
    for (const f of freqs) {
      const osc = audioCtx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.16);
    }
  } catch { /* audio not available — silent */ }
}

// ─── small helpers ───────────────────────────────────────────────────────────
function log(msg: string): void {
  const el = out('log');
  if (el) el.textContent = `${new Date().toLocaleTimeString()}  ${msg}\n` + el.textContent;
}

function setReg(state: 'on' | 'warn' | 'off', text: string): void {
  const dot = out('regDot');
  dot.className = 'reg-dot ' + state;
  out('regText').textContent = text;
}

function settingsStatus(text: string, kind: '' | 'ok' | 'err' = ''): void {
  const el = out('settingsStatus');
  el.textContent = text;
  el.className = 'status-line ' + kind;
}

// ─── views / nav ─────────────────────────────────────────────────────────────
// Scope all DOM queries to the softphone's root: the standalone page is `document`,
// but when embedded as the shared widget it's `#uc-dialer-widget` — so we never
// touch the host page's shared header <nav> (which also has `nav button`s).
const spRoot: ParentNode = document.getElementById('uc-dialer-widget') ?? document;
function showView(id: string): void {
  spRoot.querySelectorAll('.view').forEach((v) => v.classList.toggle('active', (v as HTMLElement).id === id));
  spRoot.querySelectorAll('nav button').forEach((b) =>
    b.classList.toggle('active', (b as HTMLElement).dataset.view === id),
  );
}
spRoot.querySelectorAll('nav button').forEach((b) =>
  b.addEventListener('click', () => showView((b as HTMLElement).dataset.view!)),
);

// ─── dialer keypad ───────────────────────────────────────────────────────────
function renderDial(): void {
  out('dialDisplay').textContent = dialValue;
  (out('callFab') as HTMLButtonElement).disabled = dialValue.length === 0;
  (out('backspaceBtn') as HTMLButtonElement).disabled = dialValue.length === 0;
}
document.querySelectorAll('#dialerView .key').forEach((k) =>
  k.addEventListener('click', () => {
    const d = (k as HTMLElement).dataset.k!;
    playKeyTone(d);
    dialValue += d;
    renderDial();
  }),
);
out('backspaceBtn').addEventListener('click', () => { dialValue = dialValue.slice(0, -1); renderDial(); });
out('callFab').addEventListener('click', () => {
  if (dialValue) placeCall(dialValue).catch((e) => log('CALL ERROR: ' + e.message));
});

// ─── keyboard entry ──────────────────────────────────────────────────────────
// Type a number straight into the dialer: 0-9 * # + build the number, Backspace
// deletes, Enter places the call. Only active when the dialer is showing and the
// user isn't typing into a form field (so a page's search box / dialog isn't
// hijacked) and no call overlay is up.
function dialerIsActive(): boolean {
  const dv = spRoot.querySelector('#dialerView') as HTMLElement | null;
  if (!dv || !dv.classList.contains('active')) return false;
  // In the widget, only when the panel is open; on the standalone page, always.
  const widget = document.getElementById('uc-dialer-widget');
  if (widget && !widget.classList.contains('open')) return false;
  // Not while a call overlay (incoming/active) is shown.
  const inCall = out('inCall');
  if (inCall && inCall.classList.contains('show')) return false;
  return true;
}
function isTypingTarget(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n) return false;
  const tag = n.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || n.isContentEditable === true;
}
document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;      // leave shortcuts alone
  if (isTypingTarget(e.target)) return;                 // don't hijack form fields
  if (!dialerIsActive()) return;
  const k = e.key;
  if (/^[0-9*#]$/.test(k) || k === '+') {
    dialValue += k;
    playKeyTone(k === '+' ? '0' : k);                   // no distinct tone for '+'
    renderDial();
    e.preventDefault();
  } else if (k === 'Backspace') {
    dialValue = dialValue.slice(0, -1);
    renderDial();
    e.preventDefault();
  } else if (k === 'Enter') {
    if (dialValue) placeCall(dialValue).catch((err) => log('CALL ERROR: ' + err.message));
    e.preventDefault();
  }
});

// ─── API ─────────────────────────────────────────────────────────────────────
async function api(base: string, path: string, token: string, init: RequestInit = {}): Promise<any> {
  const res = await fetch(base.replace(/\/$/, '') + path, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.status === 204 ? null : res.json();
}

// ─── session persistence ─────────────────────────────────────────────────────
function saveSession(s: Session): void { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }
function loadSession(): Session | null {
  try { const r = localStorage.getItem(SESSION_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function clearSession(): void { localStorage.removeItem(SESSION_KEY); }

// ─── connect / register ──────────────────────────────────────────────────────
/** Fresh login from the Settings form → persist a session → bring the phone up. */
async function connectFromForm(): Promise<void> {
  const apiUrl = $('apiUrl').value.replace(/\/$/, '');
  const wsUrl = $('wsUrl').value;
  settingsStatus('logging in…');
  const loginRes = await fetch(apiUrl + '/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: $('email').value, password: $('password').value,
      tenantSlug: $('tenant').value || undefined,
    }),
  });
  if (!loginRes.ok) throw new Error('login failed (' + loginRes.status + ')');
  const { token } = await loginRes.json();

  const { devices } = await api(apiUrl, '/api/devices', token);
  const wantExt = $('extension').value.trim();
  const device = devices.find((d: any) => d.extension === wantExt) ?? devices[0];
  if (!device) throw new Error('no devices in tenant');
  const creds: Credentials = await api(apiUrl, `/api/devices/${device.id}/credentials`, token);

  session = { apiUrl, wsUrl, token, deviceId: device.id, creds };
  saveSession(session);
  // Share the JWT app-wide (single sign-on) so the admin/settings pages don't
  // ask the user to log in again after they've signed into the softphone. Include
  // the role so the shared nav can hide admin-only links from a plain user.
  let role: string | null = null;
  try { role = (await api(apiUrl, '/api/auth/me', token))?.auth?.role ?? null; } catch { /* */ }
  try { localStorage.setItem('uc_session', JSON.stringify({ apiUrl, token, user: { role } })); } catch { /* */ }
  (window as any).renderNav?.();
  log(`configured ${creds.extension} → ${creds.aor}`);
  await bringUp();
}

/**
 * Gated auto-connect: use the app-wide session (shared login) to bring the phone
 * up on the LOGGED-IN USER'S OWN extension — no credential form, no extension
 * picker. Requires a valid `uc_session` (token). Resolves the user's device via
 * /api/me (scoped to them), fetches its SIP creds, and registers.
 */
async function connectFromAuth(): Promise<void> {
  const shared = (window as any).UCAuth;
  if (!shared?.token) throw new Error('not signed in');
  const apiUrl = shared.apiUrl as string;
  const token = shared.token as string;
  const wsUrl = deriveWsUrl();

  settingsStatus('connecting to your extension…');
  // /api/me returns ONLY this user's devices (by user_id) — pick their extension.
  const me = await api(apiUrl, '/api/me', token);
  localAreaCode = me?.user?.areaCode ?? null;  // for 7-digit local dialing
  const device = (me?.devices ?? [])[0];
  if (!device) {
    // No extension assigned (e.g. an admin who manages but has no phone). Not an
    // error — signal it so the caller shows a friendly state instead of failing.
    const err = new Error('no_extension') as Error & { code?: string };
    err.code = 'NO_EXTENSION';
    throw err;
  }
  const creds: Credentials = await api(apiUrl, `/api/devices/${device.id}/credentials`, token);

  session = { apiUrl, wsUrl, token, deviceId: device.id, creds };
  saveSession(session);
  $('apiUrl').value = apiUrl; $('wsUrl').value = wsUrl; $('extension').value = creds.extension;
  reflectSignedIn(true);
  log(`signed in as ${me.user?.email ?? ''} → extension ${creds.extension}`);
  await bringUp();
}

/** Derive the SIP WebSocket URL from the current origin (same rule as prefill). */
function deriveWsUrl(): string {
  const h = location.hostname;
  if (location.protocol === 'https:') return 'wss://' + location.host + '/ws';
  if (h && h !== 'localhost' && h !== '127.0.0.1') return 'ws://' + h + ':8080';
  return 'ws://localhost:8080';
}

/** Build the SimpleUser from the current session and register. Idempotent. */
async function bringUp(): Promise<void> {
  if (!session) return;
  wantRegistered = true;
  ($('realm')).value = session.creds.sipRealm;

  // Load the user's local area code (for 7-digit dialing) if we don't have it yet —
  // the session-restore path skips connectFromAuth, so fetch it here. Best-effort.
  if (localAreaCode === null) {
    try {
      const me = await api(session.apiUrl, '/api/me', session.token);
      localAreaCode = me?.user?.areaCode ?? null;
    } catch { /* non-fatal — just no 7-digit expansion */ }
  }

  // NATIVE: register via the native SIP stack (linphone in the foreground service)
  // — survives swipe/lock/doze. Skip SIP.js entirely; native events drive the UI.
  if (isNative()) {
    setReg('warn', 'connecting…');
    const host = new URL(session.apiUrl).hostname;   // phone.buildmanager.pro
    // Register over TCP/5060 (proven working). TLS/5061 is available too but
    // linphone TLS cert handling is trickier — start with TCP to get native
    // registration working, then optionally switch to tls.
    await nativeRegister({
      domain: host, port: 5060, transport: 'tcp',
      username: session.creds.sipUsername,
      password: session.creds.sipPassword,
      realm: session.creds.sipRealm,
    });
    log('native SIP registering ' + session.creds.aor);
    return;
  }

  // tear down any prior instance
  if (simpleUser) { try { await simpleUser.disconnect(); } catch { /* */ } simpleUser = null; }

  const options: SimpleUserOptions = {
    aor: session.creds.aor,
    // Send DTMF as RFC 2833 telephone-event IN the RTP stream (not SIP INFO).
    // INFO died at our B2BUA (not proxied mid-dialog) and Twilio expects 2833
    // anyway; rtpengine relays the telephone-event browser → trunk.
    sendDTMFUsingSessionDescriptionHandler: true,
    userAgentOptions: {
      // Advertise `Supported: 100rel` on our INVITEs. SIP.js defaults this to Unsupported, so
      // without it the header is absent and sip-core's asymmetric-100rel fix can't engage. With it,
      // sip-core injects `Require: 100rel` onto Twilio's (unreliable) 183 when forwarding it to us,
      // making it RELIABLE — SIP.js then PRACKs, applies the 183's SDP immediately
      // (setRemoteDescription: answer), and fires DTLS at ~t=200ms instead of waiting the whole ring
      // (~10s) for the 200 OK. THE FIX for choppy outbound early-media/MOH. See invite.ts::bridge.
      sipExtension100rel: SIPExtension.Supported,
      // ── ICE gathering: keep STUN, but don't wait the full 5s for it ──────────────────────
      // SIP.js's `iceGatheringTimeout` DEFAULTS TO 5000ms; with its default Google-STUN config the
      // browser can burn that timer gathering srflx candidates before it emits its SDP. Capping it
      // is cheap insurance, so it stays.
      //
      // ⚠️ IT IS NOT THE FIX FOR THE ~5s DTLS GAP — do not re-derive that theory. This cap was
      // added FOR that gap (the 4.95s measured then ≈ the 5000ms default, which is a COINCIDENCE
      // I pattern-matched). With the cap VERIFIED LIVE in the deployed bundle, the gap was still
      // **4.8s**: outbound call tq6vrnl609vt92k193lh — our SDP answer (Twilio's 183) reached the
      // browser at t=827.24, its DTLS ClientHello not until t=832.01. The browser sat on OUR ANSWER
      // for 4.8s; ICE gathering was already capped and finished. See SESSION_NOTES.md Session 21.
      //
      // The gap is also OUTBOUND-ONLY (inbound has DTLS up BEFORE the bridge completes), which no
      // ICE-gathering explanation predicts. Live lead: SIP.js may ignore the 183's SDP and wait for
      // the 200 OK — that would place the gap exactly across the ring window. Check
      // chrome://webrtc-internals for when setRemoteDescription actually fires.
      //
      // DO NOT set `iceServers: []` to skip the STUN round-trip. That was tried and it BROKE
      // RETRIEVE: with no STUN the browser's ONLY candidate is an mDNS `.local` hostname
      // (measured in headless Chrome: 1 candidate, `host <uuid>.local`). FreeSWITCH cannot
      // resolve .local, so connectCaller rejected the retriever's leg outright —
      //     switch_core_media.c:4158 NO candidate ACL defined, Defaulting to wan.auto
      //     switch_channel.c:3955   Hangup ... [INCOMPATIBLE_DESTINATION]
      // The srflx candidate (`srflx 64.225.61.129`) is the ONLY routable address FS gets, because
      // the drachtio_mrf sofia profile is plain-RTP (no candidate ACL, no DTLS/ICE settings).
      //
      // So: keep STUN, just cap the wait. Gathering finishes as soon as STUN answers (fast — the
      // server is reachable); the timeout only bounds the pathological case.
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 1000,
      },
      authorizationUsername: session.creds.sipUsername,
      authorizationPassword: session.creds.sipPassword,
      // Transport-level auto-reconnect (SIP.js). We layer an app-level loop too.
      reconnectionAttempts: 30,
      reconnectionDelay: 4,
    },
    media: {
      constraints: { audio: true, video: false },
      remote: { audio: out('remoteAudio') as unknown as HTMLAudioElement },
    },
    delegate: {
      // NOTE: this SIP.js delegate runs in BROWSER mode only — native returns early
      // in bringUp() and uses the native SIP stack + onNativeEvent handlers below.
      onCallReceived: () => {
        const who = safeRemoteId() ?? 'Incoming call';
        log('📞 incoming: ' + who);
        if (pendingAnswer === 'decline') { pendingAnswer = null; void declineActive(); return; }
        pendingAnswer = null;
        // In the workspace the dialer is an embedded widget that starts CLOSED — the incoming-call
        // overlay (with the Answer button) lives inside it, so we must OPEN the panel or the user
        // hears ringing but never sees a way to answer (e.g. answering a retrieved parked call).
        try { (window as any).__ucDialerOpen?.(); } catch { /* standalone page has no widget */ }
        showView('dialerView');
        showCallOverlay('incoming', who, 'Incoming call');
        startRinging();
      },
      onCallAnswered: () => {
        stopRinging();
        showCallOverlay('active', safeRemoteId() ?? 'In call', 'Connected');
      },
      onCallHangup: () => {
        stopRinging(); hideCallOverlay();
        if (wantRegistered) setReg('on', 'registered');
      },
      onServerConnect: () => { reconnectDelay = 2000; log('WS connected'); },
      onServerDisconnect: () => {
        log('WS disconnected');
        registered = false;
        setReg('warn', 'reconnecting…');
        if (wantRegistered) scheduleReconnect();
      },
      onRegistered: () => {
        registered = true;
        setReg('on', 'registered'); settingsStatus('Registered — phone is ready.', 'ok'); log('REGISTERED ✓');
        // Auto-dial a click-to-dial number (?dial= from Contacts) ONLY once truly
        // registered — this is the real "phone is ready" signal. Placing the INVITE
        // before the REGISTER 200 OK lands makes the call get torn down instantly.
        maybeAutoDial();
      },
      onUnregistered: () => { registered = false; if (wantRegistered) { setReg('warn', 'reconnecting…'); scheduleReconnect(); } },
    },
  };

  setReg('warn', 'connecting…');
  simpleUser = new SimpleUser(session.wsUrl, options);
  await simpleUser.connect();
  await simpleUser.register();

  // Push (best-effort) so a backgrounded PWA still rings (browser path).
  //
  // SKIPPED in the DESKTOP app: it shows its own native OS notification for an incoming
  // call, so the service worker's Web Push notification would be a SECOND, duplicate
  // popup for the same call. (It looked intermittent because a stale FCM subscription
  // keeps firing until the server prunes it on a 410 — hence "it stops after I dismiss
  // it a few times".) The desktop shell is always running, so it never needed push to
  // be woken up in the first place.
  if ((window as any).vocaDesktop?.isDesktop) {
    log('push skipped — desktop app uses native notifications');
  } else {
    try {
      const pushed = await subscribeToPush(session.apiUrl, session.token, session.deviceId);
      log(pushed ? 'push enabled' : 'push not enabled');
    } catch { /* non-fatal */ }
  }
  onCallAction(async (action) => {
    if (action === 'decline') { await simpleUser?.hangup().catch(() => {}); return; }
    await answerActive();
  });

  reflectSignedIn(true);
}

/** Place a queued click-to-dial (?dial=) call. Called from onRegistered — the phone
 *  is registered by then. Guarded so it fires at most once, and given a short settle
 *  delay so the transport/media stack is fully ready before the INVITE goes out. */
function maybeAutoDial(): void {
  if (!pendingDial) return;
  const number = pendingDial;
  pendingDial = null;
  dialValue = number;
  renderDial();
  showView('dialerView');
  log('click-to-dial: ' + number);
  setTimeout(() => {
    placeCall(number).catch((e) => log('CLICK-TO-DIAL ERROR: ' + (e?.message ?? e)));
  }, 400);
}

/** App-level reconnect loop: keep trying connect+register until back, with backoff. */
function scheduleReconnect(): void {
  if (reconnecting || !wantRegistered) return;
  reconnecting = true;
  const attempt = async (): Promise<void> => {
    if (!wantRegistered || !session) { reconnecting = false; return; }
    try {
      if (!simpleUser || !simpleUser.isConnected()) {
        setReg('warn', 'reconnecting…');
        await bringUp();   // idempotent: tears down the dead instance, re-registers
      } else {
        await simpleUser.register();
      }
      reconnecting = false;
      reconnectDelay = 2000;
    } catch (e: any) {
      log('reconnect failed: ' + (e?.message ?? e) + ' — retrying');
      reconnectDelay = Math.min(reconnectDelay * 1.6, 30000);
      setTimeout(attempt, reconnectDelay);
    }
  };
  setTimeout(attempt, reconnectDelay);
}

/** Keep-alive nudge: ensure we're still connected + registered, else reconnect. */
function reRegister(): void {
  if (!wantRegistered || !session) return;
  if (!simpleUser || !simpleUser.isConnected()) { scheduleReconnect(); return; }
  // Connected — refresh the registration so it never lapses.
  simpleUser.register().catch(() => scheduleReconnect());
}

// Re-register opportunities: network back / tab visible again.
window.addEventListener('online', () => { if (wantRegistered) scheduleReconnect(); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && wantRegistered) {
    if (!simpleUser || !simpleUser.isConnected()) scheduleReconnect();
  }
});

async function signOut(): Promise<void> {
  wantRegistered = false;
  clearSession();
  if (isNative()) void nativeStop();
  if (simpleUser) { try { await simpleUser.unregister(); } catch { /* */ } try { await simpleUser.disconnect(); } catch { /* */ } }
  simpleUser = null; session = null;
  setReg('off', 'signed out');
  settingsStatus('Signed out.', '');
  reflectSignedIn(false);
  // Also clear the app-wide session so signing out of the phone signs you out
  // everywhere; UCAuth.logout() redirects to the shared login.
  const shared = (window as any).UCAuth;
  if (shared?.logout) shared.logout();
}

// NATIVE events (from the native SIP stack) → drive the dialer UI. Registration,
// incoming (the native full-screen screen already rang/showed), call up/down.
onNativeEvent({
  registration: (registered, message) => {
    if (registered) { setReg('on', 'registered'); settingsStatus('Registered — phone is ready.', 'ok'); log('NATIVE REGISTERED ✓'); }
    else {
      setReg('off', 'not registered');
      // Surface the linphone failure reason so we can diagnose (cert/timeout/auth).
      settingsStatus('Registration failed: ' + (message || 'unknown'), 'err');
      log('native reg FAILED: ' + message);
    }
  },
  incomingCall: (who) => {
    // The native activity shows the full-screen ringing UI + rings. Reflect it in
    // the web UI too (in case the app is foreground).
    log('📞 native incoming: ' + who);
    showView('dialerView');
    showCallOverlay('incoming', who, 'Incoming call');
  },
  callEstablished: () => { showCallOverlay('active', 'In call', 'Connected'); },
  callEnded: () => { hideCallOverlay(); if (wantRegistered) setReg('on', 'registered'); },
});

// ─── calls ───────────────────────────────────────────────────────────────────
/** Expand a bare 7-digit LOCAL number to full E.164 using the user's area code.
 *  Only a plain 7-digit string (no +, no leading 1, not an extension) is expanded;
 *  everything else (extensions, 10/11-digit, already-+E.164) passes through as-is. */
function normalizeDialTarget(raw: string): string {
  if (raw.startsWith('sip:')) return raw;
  const digits = raw.replace(/[^\d+*#]/g, '');
  if (localAreaCode && /^[0-9]{7}$/.test(digits)) {
    return '+1' + localAreaCode + digits;
  }
  return raw;
}

async function placeCall(target: string): Promise<void> {
  if (!session) { showView('settingsView'); settingsStatus('Connect first (Settings).', 'err'); return; }
  const dialed = normalizeDialTarget(target);
  if (dialed !== target) log(`local 7-digit → ${dialed}`);
  const dest = dialed.startsWith('sip:') ? dialed : `sip:${dialed}@${session.creds.sipRealm}`;
  showCallOverlay('active', dialed, 'calling…');
  log('calling ' + dest);
  if (isNative()) { await nativeCall(dest); return; }   // native SIP places the call
  if (!simpleUser) return;
  // Attach the per-call caller-ID header when one is selected. sip-core validates the tenant
  // owns the DID before using it, so an unowned/blank value safely falls back to the default.
  // extraHeaders lives on the 3rd arg (inviterInviteOptions.requestOptions), not the 2nd.
  // `earlyMedia: true` — THE FIX FOR THE ~5s OUTBOUND AUDIO GAP. Measured, not theorised.
  //
  // SIP.js's Inviter defaults to `earlyMedia = false`, and that default means it DISCARDS the SDP in
  // a 183 Session Progress and does not call setRemoteDescription until the 200 OK. Its own source
  // spells out the behaviour ("defer getting a SDH until 2xx response is received" — inviter.js
  // ~line 519, "behavior 3b"). The browser physically cannot start DTLS before setRemoteDescription,
  // so on an outbound call it sat idle for the WHOLE RING WINDOW while rtpengine discarded Twilio's
  // audio ("SRTP output wanted, but no crypto suite was negotiated"). Heard as ~5s of choppy/absent
  // audio at the start of every outbound call, incl. MOH on an early hold.
  //
  // PROVEN with tools/outbound-dtls-probe.mjs, which hooks RTCPeerConnection in the real app:
  // across a 22s outbound call, `setRemoteDescription` NEVER FIRED — while drachtio logged
  // `received 183` at 13:29:19.201 and `sent 183` at 13:29:19.207, and rtpengine ran our answer
  // (`Received command 'answer'`) in that same millisecond. The server sent the browser a perfectly
  // good SDP answer ~3s in; SIP.js threw it away.
  //
  // NOT the earlier suspects, both disproven by measurement: it is NOT ICE gathering (capping
  // `iceGatheringTimeout` to 1000ms, verified live in the bundle, left the gap at 4.8s) and it is
  // NOT rtpengine (its MOH stream is correct: `out 693 p` vs `in 422 p` = the music IS on the wire).
  // It is also why INBOUND was always clean — as the callee the browser gets our answer immediately,
  // with no ring to wait out.
  //
  // Safe here: `earlyMedia` is only unsound when an INVITE FORKS (a WebRTC offer cannot be forked,
  // so an early dialog that loses the race to a different confirmed dialog fails the session —
  // inviter.js's `earlyMediaDialog !== session` branch). Our INVITE cannot fork: it goes to our own
  // B2BUA (sip-core), which is a single UAS and creates exactly one dialog toward the trunk.
  const inviterOptions = {
    earlyMedia: true,
    // extraHeaders must go on the INVITER CONSTRUCTOR options (2nd arg) — SIP.js bakes those
    // into the initial INVITE. The 3rd arg's requestOptions.extraHeaders is applied too late and
    // never reaches the initial INVITE on the wire (verified against SIP.js 0.21.1).
    ...(preferredCallerId ? { extraHeaders: [`X-Preferred-Caller-ID: ${preferredCallerId}`] } : {}),
  };
  await simpleUser.call(dest, inviterOptions);
}
async function answerActive(): Promise<void> {
  stopRinging();
  showCallOverlay('active', 'In call', 'connecting…');
  if (isNative()) { await nativeAnswer(); return; }
  await simpleUser?.answer().catch((e) => { log('ANSWER ERROR: ' + e.message); hideCallOverlay(); });
}
async function declineActive(): Promise<void> {
  stopRinging();
  if (isNative()) { await nativeDecline(); hideCallOverlay(); return; }
  // Browser: reject with 603 Decline (final "do not retry") rather than SIP.js's
  // default 480 (which makes Twilio retry AND looks like a dead contact to our
  // wake logic). Reach the underlying Invitation to set the status code.
  const sess: any = (simpleUser as any)?.session;
  try {
    if (sess && typeof sess.reject === 'function') await sess.reject({ statusCode: 603 });
    else await simpleUser?.decline?.();
  } catch { await simpleUser?.hangup().catch(() => {}); }
  hideCallOverlay();
}
async function hangup(): Promise<void> {
  stopRinging();
  if (isNative()) { await nativeHangup(); hideCallOverlay(); return; }
  await simpleUser?.hangup().catch(() => {});
  hideCallOverlay();
}

function safeRemoteId(): string | null {
  try { return (simpleUser as any)?.session?.remoteIdentity?.uri?.user ?? null; } catch { return null; }
}

// ─── call overlay ────────────────────────────────────────────────────────────
function showCallOverlay(mode: 'incoming' | 'active', who: string, state: string): void {
  // A LIVE call exists from the moment the 'active' overlay is shown (outbound calling, answered
  // inbound, or native established) until hideCallOverlay. This tracks the CALL, not the panel —
  // closing the dialer panel does NOT end the call, so callers (e.g. the workspace Park button)
  // must read this rather than the panel/overlay visibility. 'incoming' (ringing) is NOT in-call.
  if (mode === 'active') inCall = true;
  out('inCallWho').textContent = who;
  out('inCallState').textContent = state;
  out('incomingButtons').style.display = mode === 'incoming' ? 'flex' : 'none';
  out('activeButtons').style.display = mode === 'active' ? 'flex' : 'none';
  // reset DTMF pad each new call
  out('dtmfPad').classList.remove('show');
  out('padBtn').classList.remove('on');
  out('dtmfEcho').textContent = '';
  out('inCall').classList.add('show');
}
function hideCallOverlay(): void {
  inCall = false;
  out('inCall').classList.remove('show');
  out('dtmfPad').classList.remove('show');
  out('muteBtn').classList.remove('on');
  // Reset per-call toggles, else Hold/Park stay lit into the NEXT call (SimpleUser is reused).
  const hold = out('holdBtn'); if (hold) { hold.classList.remove('on'); delete hold.dataset['busy']; }
  out('parkBtn')?.classList.remove('on');
  dialValue = ''; renderDial();
}
out('answerBtn').addEventListener('click', () => answerActive());
out('declineBtn').addEventListener('click', () => void declineActive());
out('hangupBtn').addEventListener('click', () => hangup());
out('muteBtn').addEventListener('click', () => {
  const btn = out('muteBtn');
  const nowMuted = !btn.classList.contains('on');
  if (isNative()) { void nativeMute(nowMuted); }
  else { try { nowMuted ? (simpleUser as any)?.mute?.() : (simpleUser as any)?.unmute?.(); } catch { /* */ } }
  btn.classList.toggle('on', nowMuted);
});

/**
 * HOLD / UNHOLD — keep the caller on MY line.
 *
 * Hold vs Park: the same MEDIA operation (the far party hears MOH), but opposite OWNERSHIP.
 *   • HOLD keeps MY leg up — I intend to come back to this caller. Only I can resume them.
 *   • PARK gives the caller away: it drops my leg so ANOTHER extension can pick them up (*8N).
 * Use Park when you're handing the call off; Hold when you're keeping it.
 *
 * This is also why Hold sidesteps the SIP.js single-session limitation that makes same-phone
 * park-then-self-retrieve impossible: hold NEVER tears down our session, so there is nothing to
 * re-establish and no teardown to collide with.
 *
 * The server side already exists: SimpleUser.hold() sends a re-INVITE with a=sendonly, and
 * invite.ts's `uas.on('modify')` handler detects exactly that and plays MOH to the other party
 * (stopping it on a=sendrecv resume). So this button only has to drive SIP.js.
 */
out('holdBtn')?.addEventListener('click', () => {
  const btn = out('holdBtn') as HTMLButtonElement | null;
  if (!btn || !simpleUser) return;
  if (btn.dataset['busy'] === '1') return;                 // re-INVITE in flight — ignore double-clicks
  const goHold = !btn.classList.contains('on');
  btn.dataset['busy'] = '1';
  const done = (ok: boolean): void => {
    delete btn.dataset['busy'];
    if (ok) {
      btn.classList.toggle('on', goHold);
      try { out('inCallState').textContent = goHold ? 'On hold' : 'Connected'; } catch { /* */ }
    }
  };
  const p = goHold ? (simpleUser as any).hold?.() : (simpleUser as any).unhold?.();
  Promise.resolve(p)
    .then(() => { log(goHold ? 'hold' : 'unhold'); done(true); })
    .catch((e: any) => { log((goHold ? 'HOLD' : 'UNHOLD') + ' ERROR: ' + (e?.message ?? e)); done(false); });
});

/**
 * PARK the live call from INSIDE the in-call overlay.
 *
 * Park lives here — not on the workspace's P1–P10 grid — because clicking anything on the page
 * behind the dialer CLOSES the widget, taking the in-call overlay (and the call controls) with it.
 * Worse, the only keypad still reachable afterwards is the main dialer, and dialing from there
 * calls simpleUser.call() on a phone that already has a session: SIP.js SimpleUser is
 * single-session, so it tore down the very PSTN leg we were trying to park.
 *
 * This needs no dialing at all: POST /api/park {byExt} parks MY current call server-side, keyed on
 * my registered extension. The softphone's own leg is dropped by sip-core as part of parking, so
 * there is no session collision. (Dial-in *8<slot> pickup is still correct for RETRIEVE, where the
 * phone is idle and a new session is safe.)
 */
// NOTE: optional-chained. out() is `getElementById(...) as HTMLElement` — it LIES to the type
// system and returns null when the element is absent. This handler binds at module top level, so
// an unguarded `out('parkBtn').addEventListener` throws "Cannot read properties of null" for any
// host page whose overlay lacks the button, which aborts module evaluation — taking REGISTRATION
// with it and leaving the phone permanently unconnected. (That is exactly what happened: the
// button was added to the RETIRED index.html, while the live persistent widget is injected by
// auth.js, so every real page crashed here.)
out('parkBtn')?.addEventListener('click', () => {
  const btn = out('parkBtn') as HTMLButtonElement;
  if (!session) return;
  if (btn.classList.contains('on')) return;              // double-click guard
  btn.classList.add('on');
  const ext = session.creds.extension;
  void api(session.apiUrl, '/api/park', session.token, {
    method: 'POST',
    body: JSON.stringify({ byExt: String(ext) }),
  })
    .then((r: any) => {
      log('parked → slot ' + (r?.slot ?? '?'));
      // sip-core drops our leg as part of parking; the overlay closes on the resulting hangup.
    })
    .catch((e: any) => {
      btn.classList.remove('on');
      log('PARK ERROR: ' + (e?.message ?? e));
      try { out('inCallState').textContent = 'Park failed'; } catch { /* */ }
    });
});

// In-call DTMF keypad: toggle visibility, send tones via SIP INFO.
out('padBtn').addEventListener('click', () => {
  const pad = out('dtmfPad'); const btn = out('padBtn');
  const show = !pad.classList.contains('show');
  pad.classList.toggle('show', show);
  btn.classList.toggle('on', show);
});
document.querySelectorAll('#dtmfPad .key').forEach((k) =>
  k.addEventListener('click', () => {
    const tone = (k as HTMLElement).dataset.dtmf!;
    playKeyTone(tone);   // local audible feedback
    out('dtmfEcho').textContent = (out('dtmfEcho').textContent || '') + tone;
    if (isNative()) { void nativeDtmf(tone); return; }
    simpleUser?.sendDTMF(tone).catch((e) => log('DTMF error: ' + e.message));
  }),
);

// ─── settings buttons + first paint ──────────────────────────────────────────
function reflectSignedIn(on: boolean): void {
  const btn = out('connectBtn') as HTMLButtonElement | null;
  if (btn) btn.textContent = on ? 'Reconnect' : 'Connect & Register';
  out('signoutBtn').style.display = on ? 'block' : 'none';
  const summary = out('acctSummary');
  if (summary) {
    summary.textContent = on && session
      ? `Connected as extension ${session.creds.extension} (${session.creds.aor})`
      : '';
  }
}

out('connectBtn').addEventListener('click', () => {
  settingsStatus('connecting…');
  connectFromForm().catch((e) => { settingsStatus('Failed: ' + e.message, 'err'); setReg('off', 'error'); });
});
out('signoutBtn').addEventListener('click', () => void signOut());

// Prefill URLs (HTTPS → same-origin API + wss /ws; else direct ports).
(function prefillUrls(): void {
  const h = location.hostname;
  if (location.protocol === 'https:') {
    $('apiUrl').value = location.origin;
    $('wsUrl').value = 'wss://' + location.host + '/ws';
  } else if (h && h !== 'localhost' && h !== '127.0.0.1') {
    $('apiUrl').value = 'http://' + h + ':3020';
    $('wsUrl').value = 'ws://' + h + ':8080';
  } else {
    $('apiUrl').value = 'http://localhost:3020';
    $('wsUrl').value = 'ws://localhost:8080';
  }
})();

// Cold-start from a tapped call notification carries the intent in ?call=.
(function readCallIntent(): void {
  const params = new URLSearchParams(location.search);
  const p = params.get('call');
  // ?dial=<number> — click-to-dial from Contacts (and anywhere else): place the call
  // in-app once the phone is registered, instead of handing off to the OS dialer.
  const dial = params.get('dial');
  const cleaned = dial ? dial.replace(/[^\d+*#]/g, '') : '';
  if (cleaned) pendingDial = cleaned;
  if (p === 'answer' || p === 'decline' || cleaned) {
    if (p === 'answer' || p === 'decline') pendingAnswer = p;
    // Clean the URL so a manual reload doesn't re-trigger.
    try { history.replaceState({}, '', location.pathname); } catch { /* */ }
    // Prime the AudioContext within this notification-tap / link-click gesture so the
    // ringtone and remote audio can play (browser autoplay policy).
    try { audioCtx ??= new (window.AudioContext || (window as any).webkitAudioContext)(); void audioCtx.resume(); } catch { /* */ }
  }
})();

/** Wait for the shared /auth.js module to attach window.UCAuth (module load order
 *  vs this bundle isn't guaranteed). Resolves null if it never appears. */
function waitForAuth(timeoutMs = 3000): Promise<any> {
  return new Promise((resolve) => {
    if ((window as any).UCAuth) return resolve((window as any).UCAuth);
    const started = Date.now();
    const iv = setInterval(() => {
      if ((window as any).UCAuth) { clearInterval(iv); resolve((window as any).UCAuth); }
      else if (Date.now() - started > timeoutMs) { clearInterval(iv); resolve(null); }
    }, 30);
  });
}

// Gated boot: the SHARED login (uc_session) is the gate — ALWAYS require it first.
// A leftover softphone session must never grant access on its own (that was the
// hole: a pre-gating uc_softphone_session_v1 let the phone reconnect with no login).
(async function boot(): Promise<void> {
  const auth = await waitForAuth();
  if (!auth) {
    // auth.js failed to load — DON'T fall open. Show the settings/log with a notice;
    // no session means no registration.
    setReg('off', 'sign-in unavailable');
    showView('settingsView');
    settingsStatus('Could not load sign-in. Reload the page.', 'err');
    return;
  }

  // Gate: require a valid app-wide login (validates the token via /api/auth/me and,
  // for a `user`, this page is allowed). Blocks here until authenticated.
  setReg('off', 'sign in');
  try {
    await auth.requireLogin();
  } catch (e: any) {
    setReg('off', 'error');
    settingsStatus('Sign-in failed: ' + (e?.message ?? e), 'err');
    showView('settingsView');
    return;
  }
  (window as any).renderNav?.();

  // Authenticated. Reuse a saved softphone session ONLY if its token matches the
  // now-authenticated shared session; otherwise discard it and connect fresh.
  const saved = loadSession();
  if (saved && saved.token === auth.token) {
    session = saved;
    $('apiUrl').value = saved.apiUrl; $('wsUrl').value = saved.wsUrl;
    $('extension').value = saved.creds.extension;
    reflectSignedIn(true);
    setReg('warn', pendingAnswer ? 'answering…' : 'connecting…');
    log('restoring session — auto-registering' + (pendingAnswer ? ' (answering incoming)' : ''));
    bringUp().catch((e) => { settingsStatus('Auto-connect failed: ' + e.message, 'err'); setReg('off', 'error'); wantRegistered && scheduleReconnect(); });
    return;
  }
  if (saved) { clearSession(); log('discarded a stale phone session (token mismatch)'); }

  // Fresh: auto-connect the logged-in user's own extension.
  setReg('warn', 'connecting…');
  try {
    await connectFromAuth();
  } catch (e: any) {
    if (e?.code === 'NO_EXTENSION') {
      // Signed in, but no phone assigned (typical for an admin). Stay usable —
      // the nav + other pages work; just explain and point admins to Extensions.
      setReg('off', 'no extension');
      reflectSignedIn(true); // shows Sign out; hides the connect affordance
      const admin = auth.role === 'tenant_admin' || auth.role === 'superadmin';
      settingsStatus(
        admin
          ? 'No extension is assigned to your account yet. Create one on the Extensions page and assign it to yourself.'
          : 'No extension is assigned to your account. Ask your admin to assign one.',
        '',
      );
      showView('settingsView');
      return;
    }
    setReg('off', 'error');
    settingsStatus('Connect failed: ' + (e?.message ?? e), 'err');
    showView('settingsView');
  }
})();

void registerServiceWorker();
startKeepAlive(reRegister);

// ─── Public widget API (window.Softphone) ─────────────────────────────────────
// Lets any page drive the app-wide dialer (e.g. Contacts click-to-dial). dial()
// opens the panel and places the call — SYNCHRONOUSLY within the caller's click
// gesture when already registered, so getUserMedia(mic) is granted. If not yet
// registered, it queues the number and the onRegistered handler dials it.
const Softphone = {
  /** Open the dialer panel (if the shared widget is present). */
  open(): void { try { (window as any).__ucDialerOpen?.(); } catch { /* */ } },
  close(): void { try { (window as any).__ucDialerClose?.(); } catch { /* */ } },
  /** True once REGISTERED (ready to place a call immediately). */
  isReady(): boolean { return registered; },
  /** True while a LIVE call is in progress (from answer/outbound-active to hangup), regardless
   *  of whether the dialer panel is open. The workspace Park button reads this — the panel can be
   *  closed with the call still up. */
  isInCall(): boolean { return inCall; },
  /**
   * Set the preferred outbound caller-ID (an E.164 DID the tenant owns) for subsequent calls.
   * Pass '' to revert to the tenant default. sip-core validates ownership, so an invalid value
   * is safely ignored server-side. Lets the workspace's from-number picker choose the From.
   */
  setPreferredCallerId(e164: string): void { preferredCallerId = String(e164 ?? '').trim(); },
  /**
   * Place a call to a number (E.164 or digits). Opens the panel first. Called from a
   * user gesture (a click) so mic capture is permitted.
   */
  dial(number: string): void {
    const cleaned = String(number ?? '').replace(/[^\d+*#]/g, '');
    if (!cleaned) return;
    Softphone.open();
    dialValue = cleaned;
    try { renderDial(); } catch { /* panel may not be present */ }
    try { showView('dialerView'); } catch { /* */ }
    if (registered && simpleUser) {
      // Ready now → dial inside this gesture (mic permission flows from the click).
      log('dial (widget): ' + cleaned);
      placeCall(cleaned).catch((e) => log('DIAL ERROR: ' + (e?.message ?? e)));
    } else {
      // Not registered yet → queue; onRegistered fires maybeAutoDial().
      pendingDial = cleaned;
      log('dial queued (registering): ' + cleaned);
    }
  },
};
try { (window as any).Softphone = Softphone; } catch { /* */ }
